"use client";
import { cn } from "@/lib/utils";
import React, { ReactNode } from "react";

interface AuroraBackgroundProps extends React.HTMLProps<HTMLDivElement> {
  children: ReactNode;
  showRadialGradient?: boolean;
  /**
   * Pin the wash to the viewport instead of the wrapper's own box, so it backs
   * the entire page while content scrolls over it. One viewport-sized blurred
   * layer, rather than one per section.
   */
  fixed?: boolean;
}

export const AuroraBackground = ({
  className,
  children,
  showRadialGradient = true,
  fixed = false,
  ...props
}: AuroraBackgroundProps) => {
  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center transition-bg",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "inset-0 overflow-hidden",
          fixed ? "fixed z-0" : "absolute",
        )}
        aria-hidden="true"
      >
        <div
          className={cn(
            `
            [--white-gradient:repeating-linear-gradient(100deg,var(--white)_0%,var(--white)_7%,var(--transparent)_10%,var(--transparent)_12%,var(--white)_16%)]
            [--dark-gradient:repeating-linear-gradient(100deg,var(--black)_0%,var(--black)_7%,var(--transparent)_10%,var(--transparent)_12%,var(--black)_16%)]
            [--aurora:repeating-linear-gradient(100deg,var(--aurora-1)_10%,var(--aurora-2)_15%,var(--aurora-3)_20%,var(--aurora-4)_25%,var(--aurora-5)_30%)]
            [background-image:var(--dark-gradient),var(--aurora)]
            [background-size:300%,_200%]
            [background-position:50%_50%,50%_50%]
            filter blur-[10px]
            after:content-[""] after:absolute
            after:[background-image:var(--dark-gradient),var(--aurora)]
            after:left-0 after:top-0 after:h-full after:w-[200%] after:[background-size:100%,_50%]
            after:animate-aurora after:mix-blend-difference after:will-change-transform
            pointer-events-none
            absolute -inset-[60px] opacity-40`,
            showRadialGradient &&
              `[mask-image:radial-gradient(ellipse_140%_110%_at_50%_30%,black_45%,var(--transparent)_95%)]`,
          )}
        ></div>
      </div>
      {children}
    </div>
  );
};
